import { NumberFormatter } from '@ddt-web/data-service';
import { StringUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { IScenarioChartData } from '../../data-mapper/models';
import { StackedDataUtil } from '../../data-mapper/utils';
import { IHighChartsAttributesModel } from '../../models/high-charts-attributes.interface';
import { BaseChartWorker } from '../common';

/**
 * StackedGroupedBarWorker: chart worker specific to a Stacked Grouped Bar Chart.
 * @param {any} config JSON object representing the chart configuration.
 * @param {HighChartsAttributesModel} highChartsAttributes an object containing properties used to augment the chart configuration.
 */
export class StackedGroupedBarWorker extends BaseChartWorker {
	constructor(config: any, highChartsAttributes: IHighChartsAttributesModel) {
		super(config, highChartsAttributes);
		this.initChartForWeb();

	}

	private initChartForWeb(): void {
		this.initTooltip();
	}

	private initTooltip(): void {
		const colors = this.config.colors;
		this.config.tooltip.formatter = function (): string {
			let stackName = get(this.point, 'series.userOptions.stack') || get(this.point, 'series.stackKey', '').replace('bar', '');
			stackName = stackName.indexOf('column') > -1 ? stackName.replace('column', '') : stackName;
			const portfolioIndex = this.series.chart.options.extensibleData.portfolios.findIndex(x => x === stackName);
			const numberFormatter = new NumberFormatter('%');
			let tooltipHtml = '<div class="inner-content">';
			let sum = 0;
			const seriesLength = this.series.chart.options.extensibleData.series.length;
			this.series.chart.options.extensibleData.series.forEach((category, index) => {
				const defaultStyleOption =
					'<div ' +
					StackedDataUtil.getLegendMarkerAttributes(colors[index], this.series.chart.options.extensibleData.portfolios, portfolioIndex, category.name);
				if (category.stack === stackName) {
					sum += Number(category.data[this.point.index]);
					tooltipHtml +=
						`<div class="row no-margin text-12" style="order:${seriesLength - index}">` +
						`<div class="col-12 text-align-left col-padding-override mobile">` +
						defaultStyleOption +
						'">&#9679</div>' +
						`<span>${StringUtil.escapeHtml(category.name)}</span>` +
						`</div>` +
						`<div class="col-12 text-align-right col-padding-override mobile">` +
						`<span>${numberFormatter.format(category.data[this.point.index])}` +
						'</span>' +
						`</div>` +
						'</div>';
				}
			});
			tooltipHtml += '</div>';
			tooltipHtml += '</div>';
			let tooltipHtmlHeader = '<div class="tooltip-container stacked-tooltip">';
			tooltipHtmlHeader += '<div class="tooltip-title"><span>' + StringUtil.escapeHtml(stackName) + ' </span><span>' + numberFormatter.format(sum) + '<span></div>';
			return tooltipHtmlHeader + tooltipHtml;
		};
	}

	/**
	 * transformData: transform the data into consumable format for the scenario analysis column chart.
	 */
	public transformData(): void {
		this.transformedData = StackedDataUtil.getStackedData(this.highChartsAttributes.tabData, this.highChartsAttributes.portfolioIndex);
		const dataValues = [];
		this.transformedData.scenarios.forEach((scenario, index) => {
			if (scenario === this.highChartsAttributes.customAttributes.categoryFilter) {
				this.highChartsAttributes.tabData.tableData.forEach(table => {
					table.cells.forEach(cellCollection => {
						cellCollection.forEach(cell => {
							dataValues.push(cell.rawValue);
						});
					});
				});
				const series = this.getDataByScenario(index).reverse();
				this.config.series = series;
				const portfolios = this.getPortfolios(series);
				this.config.extensibleData = { series, portfolios };
				this.config.xAxis.categories = this.getScenarios(series);
				this.config.yAxis.max = Math.max(...dataValues);
				this.config.yAxis.min = Math.min(...dataValues);
			}
		});
	}

	/**
	 * getPortfolios: returns the unique portfolios.
	 */
	private getPortfolios(series): Array<string> {
		const portfolios = [];
		series.forEach(item => {
			if (portfolios.indexOf(item.stack) === -1) {
				portfolios.push(item.stack);
			}
		});

		return portfolios;
	}

	/**
	 * getScenarios: returns the unique scenarios.
	 */
	private getScenarios(series): Array<string> {
		const scenarios = [];
		series.forEach(item => {
			if (scenarios.indexOf(item.name) === -1) {
				scenarios.push(item.name);
			}
		});

		return scenarios;
	}

	/**
	 * getDataByScenario: gets scenario data using the index value.
	 */
	private getDataByScenario(index: number): Array<IScenarioChartData> {
		const mappedData = [];
		let dataItem = null;
		this.transformedData.series.forEach(x => {
			dataItem = {
				name: x.name,
				data: [x.data[index]],
				stack: x.stack
			};
			mappedData.push(dataItem);
		});
		return mappedData;
	}
}
